import {
  Button,
  Switch,
  type TabItem,
  type TableColumn,
  Typography,
} from "@kyan/ui";

import { LoadOrRender } from "#/components/LoadOrRender/LoadOrRender";
import { tooltips } from "#/config/tooltips";
import type { ComboInstrument } from "#/store/combo/comboInstruments";
import type { ComboType } from "#/store/combo/comboType";
import { RiskPosition } from "#/types/positions";
import {
  createOptionInstrumentName,
  createPerpInstrumentName,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { isBuy } from "#/utils/marketActionConverter";
import { formatCurrency, formatValue } from "#/utils/valueFormatters";

import { FlipDirectionToggle } from "./components/FlipDirectionToggle";
import { InstrumentName } from "./components/InstrumentName";
import { MaturitySelector } from "./components/MaturitySelector/MaturitySelector";
import { SizeInput } from "./components/SizeInput/SizeInput";
import { StrikeSelector } from "./components/StrikeSelector/StrikeSelector";

export const deleteColumnId = "deleteAll";

export const getColumns = ({
  showDeleteAll,
  glowEffectIndex,
  isRfqOptionsTab,
  isLoading,
  nettedOptionPortfolio,
  handleDelete,
  handleChange,
  handleDeleteAll,
  handleSizeChange,
}: {
  showDeleteAll: boolean;
  glowEffectIndex: number;
  isRfqOptionsTab: boolean;
  isLoading: boolean;
  nettedOptionPortfolio: RiskPosition[];
  handleDelete: (id: string) => void;
  handleChange: (
    index: number,
    field: keyof ComboInstrument,
    value: string | number | boolean,
  ) => void;
  handleDeleteAll: () => void;
  handleSizeChange: (rowId: string, value: string) => void;
}): TableColumn<ComboInstrument>[] => [
  {
    id: "instrument",
    header: "Instrument",
    minWidth: "136px",
    cell: ({ type, underlying }) => (
      <InstrumentName type={type} underlying={underlying} />
    ),
  },
  {
    id: "side",
    header: (
      <div className="flex h-3 items-center gap-1">
        <Typography>Side</Typography>
        <FlipDirectionToggle />
      </div>
    ),
    minWidth: "122px",
    cell: ({ side, type }, { rowIndex }) => (
      <Switch
        value={side}
        tabIndex={-1}
        type="market"
        className="bg-buttonBg"
        disabled={isRfqOptionsTab && type === "perps"}
        onChange={(value) => {
          handleChange(rowIndex, "side", value);
        }}
      />
    ),
  },
  {
    id: "type",
    header: "Type",
    minWidth: "122px",
    cell: ({ type, optionType }, { rowIndex }) =>
      type === "options" ? (
        <Switch
          tabIndex={-1}
          type="option"
          value={optionType}
          className="bg-buttonBg"
          onChange={(value) => {
            handleChange(rowIndex, "optionType", value);
          }}
        />
      ) : (
        "-"
      ),
  },
  {
    id: "maturity",
    header: "Maturity",
    minWidth: "138px",
    cell: ({ type, maturity }, { rowIndex }) =>
      type === "options" ? (
        <MaturitySelector
          value={maturity}
          onChange={(value) => {
            handleChange(rowIndex, "maturity", value);
          }}
        />
      ) : (
        "-"
      ),
  },
  {
    id: "strike",
    header: "Strike",
    minWidth: "138px",
    cell: ({ type, strike, maturity, underlying }, { rowIndex }) =>
      type === "options" ? (
        <StrikeSelector
          value={strike}
          maturity={maturity}
          underlying={underlying}
          onChange={(value) => {
            handleChange(rowIndex, "strike", value);
          }}
        />
      ) : (
        "-"
      ),
  },
  {
    id: "legPrice",
    header: "Leg Price",
    tooltip: tooltips.legPrice,
    cellClassName: "text-textPrimary",
    cell: ({ legPrice }) => {
      const shouldShowLoading = isLoading && isNaN(legPrice);

      return (
        <LoadOrRender isLoading={shouldShowLoading}>
          {formatCurrency(legPrice)}
        </LoadOrRender>
      );
    },
  },
  {
    id: "legRatio",
    header: "Leg Ratio",
    tooltip: tooltips.legRatio,
    cellClassName: "text-textPrimary",
    cell: ({ type, legRatio }) =>
      type === "options" ? formatValue(legRatio) : "-",
  },
  {
    id: "size",
    header: "Size",
    minWidth: "220px",
    cell: (
      { id, type, size, side, underlying, strike, maturity, optionType },
      { rowIndex },
    ) => {
      const instrument = isOptionInstrument(type)
        ? createOptionInstrumentName({
            optionType,
            underlying,
            strike,
            maturity,
          })
        : createPerpInstrumentName(underlying);

      return (
        <SizeInput
          type={type}
          value={size}
          isBuy={isBuy(side)}
          instrument={instrument}
          underlying={underlying}
          isRfqOptionsTab={isRfqOptionsTab}
          glowEffect={rowIndex === glowEffectIndex}
          nettedOptionPortfolio={nettedOptionPortfolio}
          onValueChange={(value) => {
            handleSizeChange(id, value);
          }}
        />
      );
    },
  },
  {
    id: deleteColumnId,
    header: showDeleteAll && (
      <Button
        size="sm"
        tabIndex={-1}
        variant="action"
        className="font-bold"
        onClick={handleDeleteAll}
      >
        Remove all
      </Button>
    ),
    cellClassName: "justify-end",
    columnClassName: "justify-end pr-4",
    cell: ({ id }) => (
      <Button
        size="sm"
        tabIndex={-1}
        variant="action"
        onClick={() => {
          handleDelete(id);
        }}
      >
        Remove
      </Button>
    ),
  },
];

export const tabs: TabItem<ComboType>[] = [
  {
    label: "Orderbook",
    id: "orderbook",
  },
  // {
  //   label: "RFQ - Options Only",
  //   id: "rfq-options",
  // },
];
